import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ApiError, ApiErrorType } from '../../services/api';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private getErrorMessage(error: Error): string {
    if (error instanceof ApiError) {
      switch (error.type) {
        case ApiErrorType.AUTH:
          return 'Authentication failed. Please log in again.';
        case ApiErrorType.API_KEY:
          return 'API key error. Please refresh the page or contact support.';
        case ApiErrorType.CORS:
          return 'Cross-origin request failed. Please try again later.';
        case ApiErrorType.TIMEOUT:
          return 'Request timed out. Please check your connection and try again.';
        case ApiErrorType.NETWORK:
          return 'Network error. Please check your connection.';
        case ApiErrorType.SERVER:
          return 'Server error. Please try again later.';
        case ApiErrorType.VALIDATION:
          return `Validation error: ${error.message}`;
        default:
          return error.message || 'An unknown error occurred.';
      }
    }
    return error.message || 'An unexpected error occurred.';
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary p-4 border rounded bg-light">
          <div className="alert alert-danger">
            <h4 className="alert-heading">Something went wrong</h4>
            <p>{this.state.error && this.getErrorMessage(this.state.error)}</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={this.handleRetry}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}